
















import Vue from "vue";
import Partner from "@/modules/partner/models/Partner";
import PartnerApi from "@/modules/partner/services/PartnerApi";
import configuration from "@/models/Configuration";

interface InfoRow {
  label: string;
  value: string;
  test?: string;
}

interface Data {
  partner: Partner | null;
  extCodes: string[];
  loading: boolean;
}

interface Methods {
  load(): void;
  joinParts(parts: (string | number | null | undefined)[]): string;
}

interface Computed {
  rows: InfoRow[];
  invoiceAddress: string;
  corAddress: string;
}

interface Props {
  partnerId: number;
}

/**
 * Kompakt, csak olvasható partner alapadat lista (a PartnerEditPage "Partner alap adatai" blokkjának kicsinyített változata), keret nélkül.
 * A partnerId alapján maga tölti be a partnert.
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    partnerId: { type: Number, required: true },
  },

  data() {
    return {
      partner: null,
      extCodes: [],
      loading: false,
    };
  },

  computed: {
    invoiceAddress(): string {
      const p = this.partner;
      if (!p) return "";
      if (configuration.partner.isExtendedPartnerInfoEnabled) {
        return this.joinParts([
          p.postCode,
          p.invoiceCity + ",",
          p.invStreet,
          p.invStreetTypeName,
          p.invStreetNum,
          p.invBuilding,
          p.invFloor,
          p.invDoor,
        ]);
      }
      return this.joinParts([p.postCode, p.invoiceCity + ",", p.invoiceAddress]);
    },

    corAddress(): string {
      const p = this.partner;
      if (!p) return "";
      if (configuration.partner.isExtendedPartnerInfoEnabled) {
        return this.joinParts([
          p.corPostCode,
          p.corCity + ",",
          p.corStreet,
          p.corStreetTypeName,
          p.corStreetNum,
          p.corBuilding,
          p.corFloor,
          p.corDoor,
        ]);
      }
      return this.joinParts([p.corPostCode, p.corCity + ",", p.corAddress]);
    },

    rows(): InfoRow[] {
      const p = this.partner;
      if (!p) return [];
      return [
        { label: "Partner ID", value: String(p.id), test: "info-id" },
        { label: "Partner neve", value: p.name, test: "info-name" },
        { label: "Partner név kiegészítése", value: p.nameExt },
        { label: "Rövid név", value: p.nicName },
        { label: "Levelezési név", value: p.corName },
        { label: "Vállalkozási forma", value: p.companyForm },
        { label: "Belföldi", value: p.native ? "Igen" : "Nem" },
        { label: "Számlázási cím", value: this.invoiceAddress },
        { label: "Levelezési cím", value: this.corAddress },
        { label: "Ország", value: p.countryName },
        { label: "Telefon", value: p.phoneNumber },
        { label: "Mobil", value: p.mobileNumber },
        { label: "E-mail", value: p.email },
        { label: "Ügyfél státusz", value: p.status, test: "info-status" },
        { label: "Partner típus", value: p.partnerType },
        { label: "Fizetési mód", value: p.payMode },
        { label: "Fizetési minősítés", value: p.payClass },
        { label: "Ügyfélhez tartozó pénznem", value: p.defCurrency },
        { label: "Fizetési határidő (nap)", value: p.payTerm != null ? String(p.payTerm) : "" },
        { label: "Belföldi adószám", value: p.taxAccount },
        { label: "Közösségi adószám", value: p.euTaxAccount },
        { label: "Külső azonosítók", value: this.extCodes.join(", "), test: "info-ext-codes" },
      ];
    },
  },

  watch: {
    partnerId() {
      this.load();
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      if (!this.partnerId || this.partnerId <= 1) {
        this.partner = null;
        this.extCodes = [];
        this.$emit("loaded");
        return;
      }
      const requestedId = this.partnerId;
      this.loading = true;
      try {
        const [partner, extCodes] = await Promise.all([
          PartnerApi.get(requestedId),
          PartnerApi.getExtCodes(requestedId).catch(() => [] as string[]),
        ]);
        // ha közben másik partnert választottak, a régi választ eldobjuk
        if (requestedId === this.partnerId) {
          this.partner = partner;
          this.extCodes = extCodes;
        }
      } catch {
        this.partner = null;
        this.extCodes = [];
      } finally {
        this.loading = false;
        // a szülő oldal erre várhat (pl. előtöltésnél a teljes oldalas töltés eltüntetéséhez)
        this.$emit("loaded");
      }
    },

    joinParts(parts: (string | number | null | undefined)[]): string {
      return parts
        .map((x) => (x == null ? "" : String(x).trim()))
        .filter((x) => x !== "" && x !== ",")
        .join(" ")
        .replace(/\s+,/g, ",");
    },
  },
});
